// API client para el flujo Hallazgos -> Presupuesto (Fase 2 del roadmap).
// Consume los endpoints del backend hallazgoFlujo.routes.ts:
//   POST /budgets/:id/approve
//   POST /budgets/:id/reject
//   GET  /hallazgos/:id/trazabilidad
//   POST /vouchers/:id/hallazgos
//   GET  /tipo-cambio?moneda=...&fecha=...
//
// Sigue el patron de src/api/budget.js: fetch + getAccessTokenApi + manejo
// de errores homogeneo.
import { getAccessTokenApi } from './auth';
import { BASE_PATH_SERVER } from './config';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: getAccessTokenApi(),
});

const handleError = (err) => ({
  status: 'client',
  codeMessage: 'ERROR_EN_LA_PETICION',
  message: err?.message ?? 'Error desconocido',
});

/** Aprueba un presupuesto existente y propaga etapa APROBADO a hallazgos. */
export const approveBudgetApi = async (idBudget) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}budgets/${idBudget}/approve`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        codeMessage: 'HTTP_ERROR',
        message: data?.message ?? `Error ${res.status}`,
        ...data,
      };
    }
    return data;
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Re-cotización: crea una versión nueva encadenada al presupuesto (copia items
 * + transfiere hallazgos). Devuelve { idBudget, revision }.
 */
export const createBudgetVersionApi = async (idBudget) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}budgets/${idBudget}/new-version`, {
      method: 'POST',
      headers: jsonHeaders(),
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        codeMessage: 'HTTP_ERROR',
        message: data?.message ?? `Error ${res.status}`,
        ...data,
      };
    }
    return data;
  } catch (err) {
    return handleError(err);
  }
};

/** Cadena de versiones del presupuesto (por version_group_id). */
export const getBudgetVersionsApi = async (idBudget) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}budgets/${idBudget}/versions`, {
      method: 'GET',
      headers: jsonHeaders(),
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        codeMessage: 'HTTP_ERROR',
        message: data?.message ?? `Error ${res.status}`,
        ...data,
      };
    }
    return data;
  } catch (err) {
    return handleError(err);
  }
};

/** Rechaza un presupuesto con categoria + detalle opcional. */
export const rejectBudgetApi = async (idBudget, { categoria, detalle }) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}budgets/${idBudget}/reject`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ categoria, detalle }),
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        codeMessage: 'HTTP_ERROR',
        message: data?.message ?? `Error ${res.status}`,
        ...data,
      };
    }
    return data;
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Lista hallazgos pendientes (default etapa DETECTADO) de un cliente, para
 * ofrecerlos al armar un presupuesto standalone desde el drawer.
 */
export const getHallazgosPendientesByClientApi = async (idClient, etapa) => {
  try {
    const params = new URLSearchParams({ idClient: String(idClient) });
    if (etapa) params.set('etapa', etapa);
    const res = await fetch(`${BASE_PATH_SERVER}hallazgos/pendientes?${params.toString()}`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/** Devuelve trazabilidad del hallazgo (etapa + budget + order + voucher). */
export const getHallazgoTrazabilidadApi = async (idHallazgo) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}hallazgos/${idHallazgo}/trazabilidad`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/** Crea hallazgos detectados durante una atencion de emergencia. */
export const createHallazgosFromVoucherApi = async (idVoucher, hallazgos) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}vouchers/${idVoucher}/hallazgos`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ hallazgos }),
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Consulta tipo de cambio a mindicador.cl (cache 24h backend).
 * @param {'CLP'|'UF'|'USD'} moneda
 * @param {string=} fecha  YYYY-MM-DD
 */
export const getTipoCambioApi = async (moneda, fecha) => {
  try {
    const params = new URLSearchParams({ moneda });
    if (fecha) params.set('fecha', fecha);
    const res = await fetch(`${BASE_PATH_SERVER}tipo-cambio?${params.toString()}`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 5: genera una ODT a partir de un presupuesto aprobado.
 * @param {{idBudget:number, orderManager?:string, tasks:Array}} payload
 */
export const generateOrderFromBudgetApi = async (payload) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/from-budget`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 5/6: marca una tarea como finalizada (dispara cascada hallazgo RESUELTO).
 * @param {number} idItem
 * @param {{finish:boolean, cantidad_real?:number}} payload
 */
export const finishOrderItemApi = async (idItem, payload) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}order-items/${idItem}/finish`, {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Lee una order con sus items (incluye Hallazgo nested). Usado por
 * GenerateOrderDrawer para precargar en modo edicion.
 */
export const getOrderWithItemsApi = async (idOrder) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}order/name/items?id_order=${idOrder}`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Actualiza una ODT existente mergeando tasks: id -> update, sin id -> create,
 * los items no enviados quedan ocultos (status_id=10). Propaga id_hallazgo por
 * tarea. Usado por GenerateOrderDrawer en modo edicion.
 */
export const updateOrderFromBudgetApi = async (idBudget, payload) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/from-budget/${idBudget}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAccessTokenApi(),
      },
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 6: preview de la cascada de cierre de hallazgos al finalizar una ODT.
 * Devuelve {cerrados, porCerrar, pendientes, sinHallazgo, totalTareas, tareasFinalizadas}.
 */
export const getOrderCascadePreviewApi = async (idOrder) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/cascade-preview`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 4: busca items del banco catalogo con ranking por uso.
 * @param {{q?:string, tipo?:string, moneda?:string, limit?:number}} params
 */
export const searchColeccionItemsApi = async (params = {}) => {
  try {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.tipo) qs.set('tipo', params.tipo);
    if (params.moneda) qs.set('moneda', params.moneda);
    if (params.limit) qs.set('limit', String(params.limit));
    const res = await fetch(`${BASE_PATH_SERVER}coleccion-items/search?${qs.toString()}`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 9: summary completo de un budget para el drawer de trazabilidad.
 */
export const getBudgetSummaryApi = async (idBudget) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}budgets/${idBudget}/summary`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/** Fase 9: summary completo de una ODT. */
export const getOrderSummaryApi = async (idOrder) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}orders/${idOrder}/summary`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 10: KPIs agregados del circuito hallazgos->presupuesto->odt.
 * @param {{dias?:number}} params
 */
export const getTrazabilidadKpisApi = async ({ dias } = {}) => {
  try {
    const qs = new URLSearchParams();
    if (dias) qs.set('dias', String(dias));
    const res = await fetch(`${BASE_PATH_SERVER}kpi/trazabilidad?${qs.toString()}`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/** Fase 9: summary completo de un voucher de emergencia. */
export const getVoucherSummaryApi = async (idVoucher) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}vouchers/${idVoucher}/summary`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Lista colecciones del tenant (para selects de destino al promover items).
 */
export const listColeccionesApi = async () => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}colecciones`, {
      method: 'GET',
      headers: { Authorization: getAccessTokenApi() },
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};

/**
 * Fase 4: promueve un item libre del presupuesto al catalogo.
 * @param {{nombre:string, descripcion?:string, precio?:number, costo?:number,
 *   moneda?:string, tipo_item?:string, id_coleccion:number, link_budgetitem_id?:number}} payload
 */
export const promoteItemToCatalogApi = async (payload) => {
  try {
    const res = await fetch(`${BASE_PATH_SERVER}coleccion-items/promote-from-budget`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err) {
    return handleError(err);
  }
};
